import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { EscapeKeyService } from '@core/services/escape-key.service';
import { PillColor } from './formula-variable-pill.model';

const VARIABLE_NAME_RE = /^[a-zA-Z]([a-zA-Z0-9_]*[a-zA-Z0-9])?$/;

/**
 * Inline editable variable pill used inside the formula custom input.
 *
 * The host element carries contenteditable="false" so the outer contenteditable
 * treats the entire pill as a single atomic unit (cursor jumps over it, Backspace
 * deletes the whole pill). The inner edit span overrides this when in edit mode.
 */
@Component({
  selector: 'motn-formula-variable-pill',
  templateUrl: './formula-variable-pill.component.html',
  styleUrls: ['./formula-variable-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[attr.contenteditable]': '"false"',
    '[attr.data-varname]': 'name',
    '[class.is-green]': 'color === "green" && (readonly || isNameValid)',
  },
})
export class FormulaVariablePillComponent implements OnDestroy {
  @Input() name = '';
  @Input() color: PillColor = 'red';
  /** When true: disables click-to-edit and hides the × button. */
  @Input() readonly = false;
  /** Emits when the × button is clicked. Parent removes the pill and drops the binding card. */
  @Output() removed = new EventEmitter<void>();
  @Output() renamed = new EventEmitter<string>();

  private editInputEl: HTMLInputElement | null = null;

  constructor(
    private hostEl: ElementRef<HTMLElement>,
    private escapeKeyService: EscapeKeyService,
  ) {}

  get isNameValid(): boolean {
    return VARIABLE_NAME_RE.test(this.name);
  }

  @ViewChild('editEl') set editEl(ref: ElementRef<HTMLInputElement> | undefined) {
    this.editInputEl = ref?.nativeElement ?? null;
    this.unregisterEsc(); // Angular can call this setter more than once; always deregister the old Escape handler first so it doesn't accumulate stale entries
    if (!ref) return;
    const el = ref.nativeElement;
    el.value = this.name;
    this.syncEditInputSize(el);
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    this.unregisterEsc = this.escapeKeyService.register(() => {
      el.value = this.name;
      this.onEditBlur();
    });
  }

  editing = signal(false);
  /** Teardown function returned by EscapeKeyService.register(). Call to remove the handler. No-op until edit mode is entered. */
  private unregisterEsc = () => {};

  // ── Click-outside detection ───────────────────────────────────────────────

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    if (!this.editing()) return;
    if (!this.hostEl.nativeElement.contains(event.target as Node)) {
      // .blur() doesn't reliably fire the blur event on nested contenteditable spans,
      // so call the handler directly. The guard inside prevents double-execution if
      // blur does also fire.
      this.onEditBlur();
    }
  }

  // ── Click to enter edit mode ──────────────────────────────────────────────

  onPillMousedown(event: MouseEvent): void {
    if (this.editing()) return;
    // Prevents cursor placement in the outer contenteditable; propagation kept so
    // other pills' document:mousedown handlers can exit their own edit modes.
    event.preventDefault();
  }

  onClick(event: MouseEvent): void {
    if (this.editing() || this.readonly) return;
    this.editing.set(true);
  }

  // ── Edit mode handlers ────────────────────────────────────────────────────

  onPillEditInput(event: Event): void {
    event.stopPropagation();
    this.syncEditInputSize(event.target as HTMLInputElement);
  }

  private syncEditInputSize(el: HTMLInputElement): void {
    // Fallback for browsers that don't support field-sizing: content (e.g. older Firefox)
    el.setAttribute('size', String(Math.max(1, el.value.length)));
  }

  onEditKeydown(event: KeyboardEvent): void {
    // Always stop propagation — the outer contenteditable must not react to keystrokes inside the edit input
    event.stopPropagation();
    // Block any printable character that isn't a valid variable name char
    if (event.key.length === 1 && !/^[a-zA-Z0-9_]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  }

  onEditBlur(): void {
    if (!this.editing()) return;
    const raw = (this.editInputEl?.value ?? '').trim();
    this.editing.set(false);
    this.unregisterEsc();

    if (raw && raw !== this.name) {
      this.renamed.emit(raw);
    }
  }

  // ── × remove button ───────────────────────────────────────────────────────

  onRemoveClick(event: MouseEvent): void {
    event.stopPropagation();
    this.removed.emit();
  }

  ngOnDestroy(): void {
    this.unregisterEsc();
  }
}
